import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Music, Briefcase } from 'lucide-react';
import PwaInstallButton from '../components/PwaInstallButton';

export default function LandingPage() {
  return (
    <div className="space-y-10 md:space-y-14">
      {/* Hero Section */}
      <section className="relative overflow-hidden panel py-10 md:py-16 px-6 md:px-10">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_20%,rgba(197,160,89,0.2),transparent_40%),radial-gradient(circle_at_85%_0%,rgba(128,0,0,0.35),transparent_42%)]" />

        <div className="relative text-center space-y-6 md:space-y-8">

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
            Event Booking
            <span className="block text-brand-gold">Made Effortless</span>
          </h1>

          <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto">
            Plan concerts and corporate events with Echo Jham through one clean workflow. Submit requests, monitor status updates, and coordinate faster.
          </p>

          <div className="grid grid-cols-3 max-w-xl mx-auto gap-3 text-left">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-xl font-extrabold text-brand-gold">24h</p>
              <p className="text-xs text-slate-300">Review Window</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-xl font-extrabold text-brand-gold">2 Types</p>
              <p className="text-xs text-slate-300">Concert and Corporate</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-xl font-extrabold text-brand-gold">Live</p>
              <p className="text-xs text-slate-300">Status Tracking</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
            <Link to="/login" className="btn-primary inline-flex items-center justify-center gap-2">
              Start Booking <ArrowRight size={18} />
            </Link>
            <PwaInstallButton />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="grid md:grid-cols-2 gap-6 lg:gap-8">
        <article className="panel space-y-4 hover:border-brand-gold/50 transition">
          <div className="flex items-start gap-4">
            <Music className="text-brand-gold flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="text-lg font-bold text-white mb-2">Concert Events</h3>
              <p className="text-slate-300 text-sm">
                Book a concert performance with Echo Jham. Specify your artist, budget, venue preferences, and technical requirements. Your approved booking can become a live ticketing event.
              </p>
            </div>
          </div>
        </article>

        <article className="panel space-y-4 hover:border-brand-gold/50 transition">
          <div className="flex items-start gap-4">
            <Briefcase className="text-brand-gold flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="text-lg font-bold text-white mb-2">Corporate Events</h3>
              <p className="text-slate-300 text-sm">
                Book managed corporate services including product launches, seminars, networking events. Specify attendee count, theme, and special needs. Manage internal operations seamlessly.
              </p>
            </div>
          </div>
        </article>
      </section>

      {/* How It Works */}
      <section className="panel space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white">How It Works</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { step: 1, title: 'Choose Type', desc: 'Select concert or corporate' },
            { step: 2, title: 'Fill Form', desc: 'Submit booking details' },
            { step: 3, title: 'Track Status', desc: 'Get admin review updates' },
            { step: 4, title: 'Confirm', desc: 'Booking approved' },
          ].map((item) => (
            <div key={item.step} className="text-center rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand-gold text-brand-ink font-bold mx-auto mb-3">
                {item.step}
              </div>
              <h4 className="font-semibold text-white mb-1">{item.title}</h4>
              <p className="text-sm text-slate-300">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="panel bg-[linear-gradient(90deg,rgba(128,0,0,0.8),rgba(80,0,0,0.75))] text-white text-center space-y-4 py-8">
        <h2 className="text-2xl md:text-3xl font-bold">Ready to Book Your Event?</h2>
        <p className="text-white/90 mb-6">Start now and get your request reviewed by the Echo Jham team within one business day.</p>
        <Link to="/login" className="inline-block bg-brand-gold text-brand-ink font-extrabold px-8 py-3 rounded-xl hover:brightness-95 transition">
          Get Started
        </Link>
      </section>
    </div>
  );
}
